import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { DashboardRange, DashboardSummary } from '@/types/dashboard-summary'

/** snake_case shape returned by GET /api/stats/dashboard. */
interface DashboardApi {
  range: DashboardRange
  timezone: string
  conversations: {
    total: number; today: number; week: number; mtd: number
    ai_handled: number; human_handled: number
  }
  needs_attention: { human_conversations: number; unread: number; new_orders: number }
  revenue?: {
    currency: string
    today_cents: number; week_cents: number; mtd_cents: number
    orders_today: number; orders_week: number; orders_mtd: number
  } | null
  trend: {
    granularity: 'hour' | 'day'
    points: { bucket: string; conversations: number; revenue_cents?: number | null }[]
  }
}

function mapDashboard(api: DashboardApi): DashboardSummary {
  return {
    range: api.range,
    timezone: api.timezone,
    conversations: {
      total: api.conversations.total,
      today: api.conversations.today,
      week: api.conversations.week,
      mtd: api.conversations.mtd,
      aiHandled: api.conversations.ai_handled,
      humanHandled: api.conversations.human_handled,
    },
    needsAttention: {
      humanConversations: api.needs_attention.human_conversations,
      unread: api.needs_attention.unread,
      newOrders: api.needs_attention.new_orders,
    },
    revenue: api.revenue
      ? {
          currency: api.revenue.currency,
          todayCents: api.revenue.today_cents,
          weekCents: api.revenue.week_cents,
          mtdCents: api.revenue.mtd_cents,
          ordersToday: api.revenue.orders_today,
          ordersWeek: api.revenue.orders_week,
          ordersMtd: api.revenue.orders_mtd,
        }
      : null,
    trend: {
      granularity: api.trend.granularity,
      points: api.trend.points.map((p) => ({
        bucket: p.bucket,
        conversations: p.conversations,
        revenueCents: p.revenue_cents ?? null,
      })),
    },
  }
}

/** Fetch the deployment-wide business dashboard summary for a time range. */
export function useDashboardSummary(range: DashboardRange) {
  return useQuery({
    queryKey: ['dashboard', range],
    queryFn: async () =>
      mapDashboard(await apiClient.get<DashboardApi>(`/api/stats/dashboard?range=${range}`)),
    staleTime: 60_000,
  })
}
